import React, { useMemo } from 'react';
import Select, {
  Props as ReactSelectProps,
  StylesConfig,
  GroupBase,
} from 'react-select';
import '../../../../_metronic/assets/sass/BillingStyles.scss';

export interface AppSelectOption<T = any> {
  value: T;
  label: React.ReactNode;
  icon?: React.ReactNode;
  badge?: React.ReactNode;
  description?: string;
  disabled?: boolean;
  [key: string]: any;
}

export interface AppSelectProps<OptionType = AppSelectOption, IsMulti extends boolean = false>
  extends Omit<ReactSelectProps<OptionType, IsMulti, GroupBase<OptionType>>, 'value' | 'onChange'> {
  value?: any;
  onChange?: (value: any, option: any) => void;
  options: OptionType[];
  size?: 'sm' | 'md' | 'lg';
  minWidth?: string | number;
  width?: string | number;
  portalMenu?: boolean;
}

export const AppSelect: React.FC<AppSelectProps<any, any>> = ({
  options,
  value,
  onChange,
  size = 'md',
  minWidth,
  width,
  placeholder = 'Pilih...',
  isSearchable = true,
  isClearable = false,
  portalMenu = true,
  className = '',
  styles,
  formatOptionLabel,
  ...rest
}) => {
  // Normalize value to select Option object(s)
  const normalizedValue = useMemo(() => {
    if (value === undefined || value === null || value === '') {
      return null;
    }

    if (rest.isMulti && Array.isArray(value)) {
      return options.filter((opt) => value.includes(opt.value));
    }

    // If value is already an object with value property
    if (typeof value === 'object' && value !== null && 'value' in value) {
      return value;
    }

    // Find option matching primitive value
    const found = options.find((opt) => String(opt.value) === String(value));
    return found || { value, label: String(value) };
  }, [value, options, rest.isMulti]);

  const handleChange = (selected: any, actionMeta: any) => {
    if (!onChange) return;

    if (rest.isMulti) {
      const values = Array.isArray(selected) ? selected.map((s) => s.value) : [];
      onChange(values, selected);
    } else {
      const val = selected ? selected.value : null;
      onChange(val, selected);
    }
  };

  const minHeight = size === 'sm' ? '34px' : size === 'lg' ? '48px' : '42px';
  const fontSize = size === 'sm' ? '0.82rem' : size === 'lg' ? '0.95rem' : '0.85rem';

  const customStyles: StylesConfig<any, any> = useMemo(() => {
    return {
      control: (base, state) => ({
        ...base,
        minHeight,
        height: rest.isMulti ? 'auto' : minHeight,
        fontSize,
        boxShadow: 'none',
        width: width || '100%',
        minWidth: minWidth || undefined,
        ...(styles?.control ? styles.control(base, state) : {}),
      }),
      menuPortal: (base) => ({
        ...base,
        zIndex: 99999,
        ...(styles?.menuPortal ? styles.menuPortal(base) : {}),
      }),
      menu: (base, state) => ({
        ...base,
        zIndex: 99999,
        fontSize,
        ...(styles?.menu ? styles.menu(base, state) : {}),
      }),
      menuList: (base, state) => ({
        ...base,
        maxHeight: '400px',
        ...(styles?.menuList ? styles.menuList(base, state) : {}),
      }),
      container: (base, state) => ({
        ...base,
        width: width || '100%',
        minWidth: minWidth || undefined,
        ...(styles?.container ? styles.container(base, state) : {}),
      }),
      ...styles,
    };
  }, [minHeight, fontSize, width, minWidth, rest.isMulti, styles]);

  // Default rich option formatter if option has icon/badge/description
  const renderOptionLabel = (option: any, meta: any) => {
    if (formatOptionLabel) {
      return formatOptionLabel(option, meta);
    }

    if (option.icon || option.badge || option.description) {
      return (
        <div className='d-flex align-items-center justify-content-between gap-2 w-100 py-0.5'>
          <div className='d-flex align-items-center gap-2 text-truncate'>
            {option.icon && <span className='d-inline-flex flex-shrink-0'>{option.icon}</span>}
            <span className='fw-semibold text-truncate'>{option.label}</span>
          </div>
          <div className='d-flex align-items-center gap-1.5 flex-shrink-0'>
            {option.description && (
              <span className='fs-8 text-muted d-none d-sm-inline'>{option.description}</span>
            )}
            {option.badge && <span className='d-inline-flex'>{option.badge}</span>}
          </div>
        </div>
      );
    }

    return option.label;
  };

  return (
    <Select
      options={options}
      value={normalizedValue}
      onChange={handleChange}
      placeholder={placeholder}
      isSearchable={isSearchable}
      isClearable={isClearable}
      className={`react-select-container app-select ${size === 'sm' ? 'app-select-sm' : ''} ${className}`}
      classNamePrefix='react-select'
      menuPortalTarget={portalMenu && typeof document !== 'undefined' ? document.body : undefined}
      menuPosition={portalMenu ? 'fixed' : 'absolute'}
      styles={customStyles}
      formatOptionLabel={renderOptionLabel}
      {...rest}
    />
  );
};

export default AppSelect;
